import { useEffect, useRef } from 'react';

const GLYPHS =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ$+*/<>=#?';

export function MatrixRain() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let columns = 0;
    let drops: number[] = [];
    let speeds: number[] = [];
    const fontSize = 15;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.ceil(w / fontSize);
      drops = new Array(columns).fill(0).map(() => Math.random() * (h / fontSize));
      speeds = new Array(columns).fill(0).map(() => 0.4 + Math.random() * 0.9);
    };

    resize();
    window.addEventListener('resize', resize);

    let raf = 0;
    let lastTs = 0;
    const targetMs = 1000 / 24; // ~24 fps — barato

    const draw = (ts: number) => {
      raf = requestAnimationFrame(draw);
      // El canvas está oculto en el tema neo; no gastamos CPU dibujando.
      if (document.hidden || document.documentElement.dataset.theme === 'neo') return;
      if (ts - lastTs < targetMs) return;
      lastTs = ts;

      const w = window.innerWidth;
      const h = window.innerHeight;

      // Fade del frame anterior
      ctx.fillStyle = 'rgba(2, 6, 4, 0.16)';
      ctx.fillRect(0, 0, w, h);

      ctx.font = `${fontSize}px "JetBrains Mono", monospace`;

      for (let i = 0; i < columns; i++) {
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        const ch = GLYPHS[(Math.random() * GLYPHS.length) | 0];

        // Cabeza brillante
        if (Math.random() < 0.06) {
          ctx.fillStyle = '#bfffd9';
          ctx.shadowColor = '#00ffaa';
          ctx.shadowBlur = 8;
        } else {
          ctx.fillStyle = '#00ff66';
          ctx.shadowBlur = 0;
        }
        ctx.fillText(ch, x, y);
        ctx.shadowBlur = 0;

        if (y > h + Math.random() * 200) {
          drops[i] = 0;
        } else {
          drops[i] += speeds[i];
        }
      }
    };

    if (!reduced) raf = requestAnimationFrame(draw);
    else {
      // Frame estático
      ctx.fillStyle = 'rgba(0, 255, 102, 0.08)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="matrix-rain-canvas" aria-hidden />;
}
